import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { getSyncScheduler } from '../utils/syncScheduler';

interface PWAContextType {
  isOnline: boolean;
  isPWAInstalled: boolean;
  isPWASupported: boolean;
  isInstallPromptAvailable: boolean;
  showInstallPrompt: () => void;
  registerServiceWorker: () => Promise<void>;
  unregisterServiceWorker: () => Promise<void>;
  syncPendingOperations: () => Promise<void>;
  saveOfflineData: (type: string, data: any) => Promise<void>;
  getOfflineData: (type: string) => Promise<any[]>;
  clearOfflineData: (type?: string) => Promise<void>;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  subscribeToPushNotifications: () => Promise<void>;
  unsubscribeFromPushNotifications: () => Promise<void>;
  isSubscribedToPush: boolean;
  networkStatus: 'online' | 'offline' | 'slow';
  lastSyncTime: Date | null;
  // Enhanced sync functionality
  addSyncOperation: (operation: any) => string;
  removeSyncOperation: (id: string) => boolean;
  getSyncOperations: () => any[];
  getSyncOperation: (id: string) => any | undefined;
  getConnectionQuality: () => any;
  getUserActivity: () => any;
  getBatteryStatus: () => any;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

interface PWAProviderProps {
  children: React.ReactNode;
}

export const PWAProvider: React.FC<PWAProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [isPWASupported, setIsPWASupported] = useState(false);
  const [isInstallPromptAvailable, setIsInstallPromptAvailable] = useState(false);
  const [isSubscribedToPush, setIsSubscribedToPush] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<'online' | 'offline' | 'slow'>('online');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  
  const installPromptEvent = useRef<any>(null);
  const serviceWorkerRegistration = useRef<ServiceWorkerRegistration | null>(null);

  // Check if PWA is supported
  useEffect(() => {
    const checkPWASupport = () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      setIsPWASupported(supported);
      
      // Check if already installed
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        setIsPWAInstalled(true);
      }
    };

    checkPWASupport();
  }, []);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkStatus('online');
      // Trigger sync when coming back online
      syncPendingOperations();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkStatus('offline');
    };

    const handleConnectionChange = () => {
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        if (connection) {
          const effectiveType = connection.effectiveType;
          if (effectiveType === 'slow-2g' || effectiveType === '2g') {
            setNetworkStatus('slow');
          } else if (effectiveType === '3g' || effectiveType === '4g') {
            setNetworkStatus('online');
          }
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('connection', handleConnectionChange);

    // Initial check
    handleConnectionChange();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('connection', handleConnectionChange);
    };
  }, []);

  // Listen for install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      installPromptEvent.current = e;
      setIsInstallPromptAvailable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Check push notification subscription
  useEffect(() => {
    const checkPushSubscription = async () => {
      if (!('serviceWorker' in navigator)) return;
      
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribedToPush(!!subscription);
      } catch (error) {
        console.error('Error checking push subscription:', error);
      }
    };

    checkPushSubscription();
  }, []);

  const showInstallPrompt = useCallback(async () => {
    if (!installPromptEvent.current) return;

    try {
      const result = await installPromptEvent.current.prompt();
      if (result.outcome === 'accepted') {
        setIsInstallPromptAvailable(false);
        setIsPWAInstalled(true);
      }
    } catch (error) {
      console.error('Install prompt failed:', error);
    }
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      
      serviceWorkerRegistration.current = registration;
      console.log('Service Worker registered:', registration);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }, []);

  const unregisterServiceWorker = useCallback(async () => {
    if (!serviceWorkerRegistration.current) return;

    try {
      const success = await serviceWorkerRegistration.current.unregister();
      if (success) {
        console.log('Service Worker unregistered');
        serviceWorkerRegistration.current = null;
      }
    } catch (error) {
      console.error('Service Worker unregistration failed:', error);
    }
  }, []);

  const syncPendingOperations = useCallback(async () => {
    if (!serviceWorkerRegistration.current) return;

    try {
      const registration = serviceWorkerRegistration.current;
      if ('sync' in registration) {
        await registration.sync.register('background-sync');
        setLastSyncTime(new Date());
      }
    } catch (error) {
      console.error('Background sync failed:', error);
    }
  }, []);

  const saveOfflineData = useCallback(async (type: string, data: any) => {
    if (!serviceWorkerRegistration.current) return;

    try {
      const messageChannel = new MessageChannel();
      const port1 = messageChannel.port1;
      const port2 = messageChannel.port2;

      serviceWorkerRegistration.current.active?.postMessage({
        type: 'save-offline-data',
        data: { type, data }
      }, [port2]);

      return new Promise((resolve, reject) => {
        port1.onmessage = (event) => {
          if (event.data.success) {
            resolve(event.data.result);
          } else {
            reject(new Error(event.data.error));
          }
          port1.close();
        };

        // Timeout after 5 seconds
        setTimeout(() => {
          reject(new Error('Timeout saving offline data'));
          port1.close();
        }, 5000);
      });
    } catch (error) {
      console.error('Failed to save offline data:', error);
      throw error;
    }
  }, []);

  const getOfflineData = useCallback(async (type: string) => {
    if (!serviceWorkerRegistration.current) return [];

    try {
      const messageChannel = new MessageChannel();
      const port1 = messageChannel.port1;
      const port2 = messageChannel.port2;

      serviceWorkerRegistration.current.active?.postMessage({
        type: 'get-offline-data',
        data: { type }
      }, [port2]);

      return new Promise((resolve, reject) => {
        port1.onmessage = (event) => {
          resolve(event.data);
          port1.close();
        };

        setTimeout(() => {
          reject(new Error('Timeout getting offline data'));
          port1.close();
        }, 5000);
      });
    } catch (error) {
      console.error('Failed to get offline data:', error);
      return [];
    }
  }, []);

  const clearOfflineData = useCallback(async (type?: string) => {
    if (!serviceWorkerRegistration.current) return;

    try {
      const db = await openDatabase();
      const transaction = db.transaction(['offline_data'], 'readwrite');
      const store = transaction.objectStore('offline_data');

      if (type) {
        // Clear specific type
        const index = store.index('type');
        const request = index.openCursor(type);
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            cursor.continue();
          }
        };
      } else {
        // Clear all
        store.clear();
      }
    } catch (error) {
      console.error('Failed to clear offline data:', error);
    }
  }, []);

  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }, []);

  const subscribeToPushNotifications = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications not supported');
    }

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    const registration = await navigator.serviceWorker.ready;
    
    // Generate VAPID keys or use your server's public key
    const applicationServerKey = urlBase64ToUint8Array(process.env.VITE_VAPID_PUBLIC_KEY || '');
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });

    // Send subscription to server
    await fetch('/api/notifications/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify(subscription)
    });

    setIsSubscribedToPush(true);
  }, [requestNotificationPermission]);

  const unsubscribeFromPushNotifications = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      setIsSubscribedToPush(false);
    }
  }, []);

  // IndexedDB helper function
  const openDatabase = () => {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('gh-town-offline-db', 1);
      
      request.onerror = () => reject(request.error);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        if (!db.objectStoreNames.contains('offline_data')) {
          const store = db.createObjectStore('offline_data', { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
      
      request.onsuccess = () => resolve(request.result);
    });
  };

  // Utility function to convert VAPID key
  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const contextValue: PWAContextType = {
    isOnline,
    isPWAInstalled,
    isPWASupported,
    isInstallPromptAvailable,
    showInstallPrompt,
    registerServiceWorker,
    unregisterServiceWorker,
    syncPendingOperations,
    saveOfflineData,
    getOfflineData,
    clearOfflineData,
    requestNotificationPermission,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    isSubscribedToPush,
    networkStatus,
    lastSyncTime,
    // Enhanced sync functionality
    addSyncOperation: (operation) => getSyncScheduler().addOperation(operation),
    removeSyncOperation: (id) => getSyncScheduler().removeOperation(id),
    getSyncOperations: () => getSyncScheduler().getOperations(),
    getSyncOperation: (id) => getSyncScheduler().getOperation(id),
    getConnectionQuality: () => getSyncScheduler().getConnectionQuality?.(),
    getUserActivity: () => getSyncScheduler().getUserActivity?.(),
    getBatteryStatus: () => getSyncScheduler().getBatteryStatus?.()
  };

  return (
    <PWAContext.Provider value={contextValue}>
      {children}
    </PWAContext.Provider>
  );
};